import React, { createContext, ReactNode, useContext, useRef, useState } from "react";
import { Box, Divider, Stack } from "@mui/material";
import AiChatHistory from "../components/AiChat/AiChatHistory";
import { useCase } from "./CaseProvider";
import AiChatThreadMessages from "../components/AiChat/AiChatThreadMessages";
import AIChatInput from "../components/AiChat/AIChatInput";
import { getAiAgentByUser, sendAiChatPrompt } from "../services/aiChatService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource } from "../services/ReactQuery/reactQueryService";
import { useAiEventStreamSnapshot } from "../hooks/useAiEventStream";
import { useAiEventStreamRegistry } from "./AiEventStreamProvider";
import CollapsableWindow, { CollapsableWindowProps } from "../components/CollapsableWindow/CollapsableWindow";
import { useCurrentUser } from "./CurrentUserProvider";
import FloatingAgentButton from "../components/AiChat/FloatingAgentButton";
import AiChatToolbar from "../components/AiChat/AiChatToolbar";
import { AiChatAgent } from "../types/AIChat/AiChatThread";
import { useDrawer } from "./DrawerProvider";
import { DrawerState } from "../types/DrawerState";
import { AiChatMessage } from "../types/AIChat/AiChatMessage";
import { AiChatFeedbackRating } from "../types/AIChat/AiChatFeedback";
import AiChatFeedbackSheet from "../components/AiChat/AiChatFeedbackSheet";
import AiChatFaviconController from "../components/AiChat/AiChatFaviconController.ts";

type AiChatProviderData = {
  openAiChat: (initialMessage: string) => void;
  isOpen: boolean;
  isLoading: boolean;
  isCancellationRequested: boolean;
  setIsCancellationRequested: (threadId: number, isRequested: boolean) => void;
  setActiveThreadId: React.Dispatch<React.SetStateAction<number | null>>;
  setHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCreatingThread: React.Dispatch<React.SetStateAction<boolean>>;
  activeThreadId: number | null;
  resetAiChat: (closeWindow?: boolean) => void;
  activeAgent: AiChatAgent;
  //execution step currently being edited in a drawer; the chat treats prompts as edit instructions for it
  activeEditStepId: number | null;
  openStepEditDrawer: (stepId: number, stepDrawerState: DrawerState) => Promise<void>;
  closeStepEditDrawer: () => Promise<boolean>;
  openFeedback: (message: AiChatMessage, rating: AiChatFeedbackRating) => void;
  editMessage: (message: AiChatMessage, content: string) => Promise<void>;
};

type AiChatFeedbackTarget = {
  message: AiChatMessage;
  rating: AiChatFeedbackRating;
};

const AiChatProviderContext = createContext<AiChatProviderData | null>(null);

export const AiChatProvider = ({ children }: { children: ReactNode }) => {
  const { product } = useCase();
  if (!product) {
    return null;
  }
  return <AiChatProviderInner key={"aiChat" + product.id}>{children}</AiChatProviderInner>;
};

const AiChatProviderInner = ({ children }: { children: ReactNode }) => {
  const [windowState, setWindowState] = useState<CollapsableWindowProps["windowState"]>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [isCreatingThread, setIsCreatingThread] = useState(false);
  const [cancellingThreadIds, setCancellingThreadIds] = useState<number[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<number | null>(null);
  const chatWrapperRef = useRef<HTMLDivElement>(null);
  const aiInputRef = useRef<HTMLTextAreaElement>(null);
  const { product } = useCase();
  const currentUser = useCurrentUser();
  const createMutation = useMutation({
    mutationFn: createResource,
  });
  const queryClient = useQueryClient();
  const { getStore: getStreamStore } = useAiEventStreamRegistry();
  const stream = useAiEventStreamSnapshot(activeThreadId);
  const isCancellationRequested = activeThreadId !== null && cancellingThreadIds.includes(activeThreadId);
  const setIsCancellationRequested = (threadId: number, isRequested: boolean) => {
    setCancellingThreadIds((previous) =>
      isRequested ? [...previous, threadId] : previous.filter((id) => id !== threadId),
    );
  };
  // Completion events can precede persistence; the submitting request resets its store after refresh.
  const isLoading =
    isCancellationRequested ||
    (activeThreadId === null ? isCreatingThread : stream.streamId !== null && stream.status !== "error");
  const drawer = useDrawer();
  const [activeEditStepId, setActiveEditStepId] = useState<number | null>(null);
  const [feedbackTarget, setFeedbackTarget] = useState<AiChatFeedbackTarget | null>(null);

  const closeFeedback = () => setFeedbackTarget(null);

  const setHistoryOpenExtended: AiChatProviderData["setHistoryOpen"] = (nextState) => {
    const nextHistoryOpen = typeof nextState === "function" ? nextState(historyOpen) : nextState;
    if (nextHistoryOpen) {
      closeFeedback();
    }
    setHistoryOpen(nextHistoryOpen);
  };

  const openFeedback = (message: AiChatMessage, rating: AiChatFeedbackRating) => {
    setHistoryOpen(false);
    setFeedbackTarget({ message, rating });
  };

  // The drawer's onClose clears activeEditStepId on every close or replace path, so the id cannot go stale.
  const openStepEditDrawer = async (stepId: number, stepDrawerState: DrawerState) => {
    if (await drawer.setDrawerState({ ...stepDrawerState, onClose: () => setActiveEditStepId(null) })) {
      setActiveEditStepId(stepId);
    }
  };

  // This closes the step drawer before the chat closes or changes threads, so it cannot show old data.
  // Pending document changes are saved before the form is removed.
  const closeStepEditDrawer = () => {
    if (activeEditStepId !== null) {
      return drawer.closeDrawer();
    }
    return Promise.resolve(true);
  };

  const changeActiveThread = async (nextThreadId: React.SetStateAction<number | null>) => {
    if (activeEditStepId !== null && !(await closeStepEditDrawer())) {
      return false;
    }
    closeFeedback();
    setActiveThreadId(nextThreadId);
    return true;
  };

  const activeAgent = getAiAgentByUser(currentUser);

  const resetAiChat = async (closeWindow = true) => {
    if (!(await changeActiveThread(null))) {
      return;
    }
    setHistoryOpenExtended(false);
    if (closeWindow) {
      setWindowState(null);
    }
  };

  if (!product) {
    return null;
  }

  if (!activeAgent) {
    return children;
  }

  const openAiChat = async (initialMessage?: string) => {
    setWindowState("maximized");
    if (initialMessage) {
      if (!(await changeActiveThread(null))) {
        return;
      }
      await sendAiChatPrompt({
        product,
        activeThreadId: null,
        setActiveThreadId: changeActiveThread,
        createMutation,
        queryClient,
        getStreamStore,
        prompt: initialMessage,
        setIsCreatingThread,
        agentType: activeAgent,
      });
    }
  };

  const editMessage = async (message: AiChatMessage, content: string) => {
    if (activeThreadId === null) {
      throw new Error("An active AI chat thread is required to edit a message.");
    }

    await sendAiChatPrompt({
      product,
      activeThreadId,
      setActiveThreadId: changeActiveThread,
      createMutation,
      queryClient,
      getStreamStore,
      prompt: content,
      setIsCreatingThread,
      agentType: activeAgent,
      editedMessageUuid: message.uuid,
    });
  };

  const data: AiChatProviderData = {
    openAiChat,
    isOpen: ["maximized", "minimized"].includes(windowState || ""),
    setActiveThreadId: changeActiveThread,
    activeThreadId,
    setHistoryOpen: setHistoryOpenExtended,
    resetAiChat,
    setIsCreatingThread,
    isLoading,
    isCancellationRequested,
    setIsCancellationRequested,
    activeAgent,
    activeEditStepId,
    openStepEditDrawer,
    closeStepEditDrawer,
    openFeedback,
    editMessage,
  };

  return (
    <AiChatProviderContext.Provider value={data}>
      <AiChatFaviconController />
      <CollapsableWindow windowState={windowState} elementToFocusRef={aiInputRef}>
        <AiChatToolbar windowState={windowState} setWindowState={setWindowState} />
        <Stack ref={chatWrapperRef} sx={{ flexGrow: 1, position: "relative", overflow: "hidden" }}>
          <Box sx={{ flexGrow: 1, padding: "1rem", overflowY: "auto" }}>
            <AiChatThreadMessages />
          </Box>
          <Divider />
          <AIChatInput ref={aiInputRef} />
          {feedbackTarget && activeThreadId && (
            <AiChatFeedbackSheet
              key={`${feedbackTarget.message.id}:${feedbackTarget.rating}`}
              message={feedbackTarget.message}
              rating={feedbackTarget.rating}
              activeThreadId={activeThreadId}
              chatWrapperRef={chatWrapperRef}
              onClose={closeFeedback}
            />
          )}
          <AiChatHistory
            open={historyOpen}
            closeHistory={() => setHistoryOpenExtended(false)}
            chatWrapperRef={chatWrapperRef}
            backofficeCaseIri={product.backofficeCase["@id"]}
          />
        </Stack>
      </CollapsableWindow>
      <Box sx={{ position: "fixed", bottom: "3rem", right: "10rem", zIndex: 100 }}>
        {!windowState && <FloatingAgentButton disabled={!!windowState} onClick={() => setWindowState("maximized")} />}
      </Box>
      {children}
    </AiChatProviderContext.Provider>
  );
};

export const useAiChat = () => {
  const context = useContext(AiChatProviderContext);
  if (!context) {
    throw new Error("useAiChat must be used within a AiChatProvider");
  }
  return context;
};

export const useOptionalAiChat = () => {
  return useContext(AiChatProviderContext) || null;
};
